import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	SovosButton,
	SovosCheckbox,
	SovosCircularProgress,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosFormControlLabel,
	SovosFormGroup,
	SovosLinearProgress,
	SovosPageContentGeneric,
	SovosPageLayout,
	SovosRadioGroup,
	SovosSnackbar,
	SovosTextField,
	SovosTypography,
} from 'mosaic-react';
import React from 'react';
import { useState } from 'react';

import {
	fetchAllOutputFormats,
	fetchConfiguration,
	updateConfiguration,
} from 'src/api/configuration';
import { REP_EXTENSION, TXT_EXTENSION } from 'src/helpers/constant';
import useBlocker from 'src/hooks/useBlocker';
import type { SettingsConfiguration } from 'src/interfaces/Configuration';
import type { RouteExitConfirmationDialogState } from 'src/interfaces/RouteExitConfirmationDialogState';

import styles from './SubscriptionSettingsPage.module.scss';

type SelectedOutputFormatsRecord = Record<number, boolean>;

const SubscriptionSettingsPage: React.FC = () => {
	const queryClient = useQueryClient();

	const allOutputFormatsQuery = useQuery(
		['allOutputFormats'],
		fetchAllOutputFormats
	);

	const configurationQuery = useQuery(['configuration'], fetchConfiguration, {
		onSuccess: (configuration) => {
			if (!savedConfiguration || !hasChanges) {
				setFieldsToSavedConfiguration(configuration);
			}
		},
	});

	const updateConfigurationMutation = useMutation(
		() =>
			updateConfiguration({
				gscLimit,
				stateLimit,
				selectedOutputTypes: Object.entries(fileFormats)
					.filter(([_, checked]) => checked)
					.map(([id]) => Number(id)),
				xstoreSettings: {
					useProductCode: xstoreSettings.useProductCode,
					gtdEntityUseCode: xstoreSettings.isGtdEntityUseCode,
					goodServiceCodeMapping: !xstoreSettings.useProductCode
						? xstoreSettings.isGoodServiceCodeMapping
						: false,
					extension: xstoreSettings.extension,
				},
			}),
		{
			onSuccess: (configuration) => {
				queryClient.setQueryData(['configuration'], configuration);

				setFieldsToSavedConfiguration(configuration);

				setSnackbar({
					message: 'Changes saved.',
					variant: 'confirmation',
					open: true,
				});
			},
			onError: () =>
				setSnackbar({
					message: 'An error occurred, please try again.',
					variant: 'error',
					open: true,
				}),
		}
	);

	const [savedConfiguration, setSavedConfiguration] = useState<
		SettingsConfiguration | undefined
	>(configurationQuery.data);

	const [gscLimit, setGscLimit] = useState(
		savedConfiguration?.gscLimit?.toString() || ''
	);
	const [stateLimit, setStateLimit] = useState(
		savedConfiguration?.stateLimit?.toString() || ''
	);
	const [fileFormats, setFileFormats] = useState<SelectedOutputFormatsRecord>(
		savedConfiguration?.outputTypes
			? savedConfiguration.outputTypes.reduce<SelectedOutputFormatsRecord>(
					(selectedOutputs, output) => {
						if (output.enabled === 'Y') {
							selectedOutputs[output.id] = true;
						}
						return selectedOutputs;
					},
					{}
			  ) || {}
			: {}
	);

	const [xstoreSettings, setXstoreSettings] = useState<{
		isGtdEntityUseCode: boolean;
		isGoodServiceCodeMapping: boolean;
		useProductCode: boolean;
		extension: string;
	}>({
		isGtdEntityUseCode:
			savedConfiguration?.xstoreSettings?.gtdEntityUseCode || false,
		useProductCode: savedConfiguration?.xstoreSettings?.useProductCode || false,
		isGoodServiceCodeMapping:
			savedConfiguration?.xstoreSettings?.goodServiceCodeMapping || false,
		extension: savedConfiguration?.xstoreSettings?.extension || REP_EXTENSION,
	});

	const [snackbar, setSnackbar] = useState<{
		message: string;
		variant?: 'confirmation' | 'error';
		open: boolean;
	}>({
		message: '',
		open: false,
	});

	const hasChanges =
		savedConfiguration &&
		((savedConfiguration.gscLimit?.toString() || '') !== gscLimit ||
			(savedConfiguration.stateLimit?.toString() || '') !== stateLimit ||
			savedConfiguration.outputTypes.length !==
				Object.values(fileFormats).filter(Boolean).length ||
			savedConfiguration.outputTypes.some(({ id }) => !fileFormats[id]) ||
			savedConfiguration?.xstoreSettings.goodServiceCodeMapping !==
				xstoreSettings.isGoodServiceCodeMapping ||
			savedConfiguration?.xstoreSettings.useProductCode !==
				xstoreSettings.useProductCode ||
			savedConfiguration?.xstoreSettings.gtdEntityUseCode !==
				xstoreSettings.isGtdEntityUseCode ||
			savedConfiguration?.xstoreSettings.extension !==
				xstoreSettings.extension);

	const isValid =
		!isNaN(Number(gscLimit)) &&
		!isNaN(Number(stateLimit)) &&
		Number(stateLimit) <= 60 &&
		Object.values(fileFormats).filter(Boolean).length > 0;

	function setFieldsToSavedConfiguration(configuration: SettingsConfiguration) {
		setSavedConfiguration(configuration);
		setGscLimit(configuration.gscLimit?.toString() || '');
		setStateLimit(configuration.stateLimit?.toString() || '');
		setFileFormats(
			configuration.outputTypes.reduce<Record<number, boolean>>(
				(selectedOutputs, output) => {
					if (output.enabled === 'Y') {
						selectedOutputs[output.id] = true;
					}
					return selectedOutputs;
				},
				{}
			)
		);
		setXstoreSettings({
			isGoodServiceCodeMapping:
				configuration?.xstoreSettings.goodServiceCodeMapping,
			useProductCode: configuration?.xstoreSettings.useProductCode,
			isGtdEntityUseCode: configuration?.xstoreSettings.gtdEntityUseCode,
			extension: configuration?.xstoreSettings.extension,
		});
	}

	const [exitConfirmationDialog, setExitConfirmationDialog] =
		useState<RouteExitConfirmationDialogState>({
			isOpen: false,
			continue: null,
		});

	useBlocker(
		(tx) => setExitConfirmationDialog({ isOpen: true, continue: tx.retry }),
		Boolean(hasChanges && !exitConfirmationDialog.isOpen)
	);

	const isXstoreFileSelected = (): boolean => {
		const xStoreOutputFormat = allOutputFormatsQuery.data?.find(
			(outputFormat) => outputFormat.name.includes('xStore')
		);

		for (let key in fileFormats) {
			if (Number(key) === xStoreOutputFormat?.id) return fileFormats[key];
		}

		return false;
	};

	return (
		<SovosPageLayout
			pageTitle='Subscription Settings'
			titleBarActions={[
				<SovosButton
					key='saveButton'
					onClick={() => updateConfigurationMutation.mutate()}
					disabled={
						!(isValid && hasChanges) || updateConfigurationMutation.isLoading
					}
				>
					{updateConfigurationMutation.isLoading ? (
						<SovosCircularProgress size={20} />
					) : (
						'Save'
					)}
				</SovosButton>,
			]}
		>
			<SovosPageContentGeneric>
				{!allOutputFormatsQuery.isLoading &&
				allOutputFormatsQuery.data &&
				!configurationQuery.isLoading &&
				configurationQuery.data &&
				savedConfiguration ? (
					<>
						<SovosTypography variant='h5' sx={{ mb: 1 }}>
							Good/Service code limits
						</SovosTypography>
						<SovosTypography variant='body2'>
							Enter the maximum number of codes that can be uploaded in a file.
							Leaving this field blank will allow unlimited number of codes.
						</SovosTypography>
						<SovosTextField
							inputMode='numeric'
							value={gscLimit}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								const value = event.target.value;
								if (value === '' || value.match(/^[1-9][0-9]*$/)) {
									setGscLimit(value);
								}
							}}
							label='Good/Service code limit'
							sx={{ mt: 2, mb: 2 }}
						/>

						<SovosTypography variant='h5' sx={{ mb: 1 }}>
							State limits
						</SovosTypography>
						<SovosTypography variant='body2'>
							Enter the maximum number of states a rate file can contain.
							Leaving this field blank will allow unlimited number of states.
						</SovosTypography>
						<SovosTextField
							inputMode='numeric'
							value={stateLimit}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								const value = event.target.value;

								if (
									value === '' ||
									(/^[1-9][0-9]*$/.test(value) && Number(value) <= 60)
								) {
									setStateLimit(value);
								}
							}}
							label='State limit'
							helperText='Maximum limit is 60'
							sx={{ mt: 2, mb: 3 }}
						/>

						<SovosTypography variant='h5' sx={{ mb: 1 }}>
							Rate file format options
						</SovosTypography>
						<SovosTypography variant='body2'>
							Select the file options the user creating rate files will be able
							to choose from. At least one format is required.
						</SovosTypography>
						{Object.values(fileFormats).filter(Boolean).length === 0 && (
							<SovosTypography variant='body2' sx={{ color: 'error.main' }}>
								*Required
							</SovosTypography>
						)}

						<SovosFormGroup
							sx={{ ml: 1, '& > label': { width: 'fit-content' } }}
						>
							{allOutputFormatsQuery.data.map((outputFormat) => (
								<SovosFormControlLabel
									key={outputFormat.id}
									control={
										<SovosCheckbox
											checked={fileFormats[outputFormat.id]}
											onChange={(
												event: React.ChangeEvent<HTMLInputElement>
											) => {
												setFileFormats((fileFormats) => ({
													...fileFormats,
													[outputFormat.id]: event.target.checked,
												}));
												!isXstoreFileSelected() &&
													setXstoreSettings({
														isGtdEntityUseCode: false,
														useProductCode: true,
														isGoodServiceCodeMapping: false,
														extension: REP_EXTENSION,
													});
											}}
										/>
									}
									label={outputFormat.name}
								/>
							))}
						</SovosFormGroup>

						{isXstoreFileSelected() && (
							<>
								<SovosTypography variant='h5' sx={{ mt: 2, mb: 2 }}>
									xStore file additional settings
								</SovosTypography>
								<SovosTypography variant='body1' sx={{ mb: 1 }}>
									Organize file by
								</SovosTypography>
								<SovosRadioGroup
									row
									options={[
										{
											label: 'Product codes',
											value: 'true',
										},
										{
											label: 'Sovos Good/Service codes',
											value: 'false',
										},
									]}
									value={xstoreSettings.useProductCode}
									onChange={(_: any, value: string) => {
										setXstoreSettings({
											...xstoreSettings,
											useProductCode: !!(
												value && value.toLowerCase() === 'true'
											),
										});
									}}
									sx={{ mb: 2, ml: 1 }}
								/>
								<SovosFormGroup sx={{ ml: -0.5 }}>
									<div className={styles.xStoreAdditionalSettingsCheckboxes}>
										<SovosCheckbox
											checked={xstoreSettings.isGtdEntityUseCode}
											onChange={(
												event: React.ChangeEvent<HTMLInputElement>
											) => {
												setXstoreSettings({
													...xstoreSettings,
													isGtdEntityUseCode: event.target.checked,
												});
											}}
										/>
										<span className={styles.text}>
											Include reasons for exemptions
										</span>
									</div>
								</SovosFormGroup>
								{!xstoreSettings.useProductCode && (
									<SovosFormGroup sx={{ ml: -0.5 }}>
										<div className={styles.xStoreAdditionalSettingsCheckboxes}>
											<SovosCheckbox
												checked={xstoreSettings.isGoodServiceCodeMapping}
												onChange={(
													event: React.ChangeEvent<HTMLInputElement>
												) => {
													setXstoreSettings({
														...xstoreSettings,
														isGoodServiceCodeMapping: event.target.checked,
													});
												}}
											/>
											<span className={styles.text}>
												Include Good/Service code mapping
											</span>
										</div>
									</SovosFormGroup>
								)}
								<SovosTypography variant='body1' sx={{ mt: 2, mb: 1 }}>
									Select file extension for xStore file
								</SovosTypography>
								<SovosTypography variant='body2' sx={{ mb: 1 }}>
									What format would you like the xStore file to be generated as?
								</SovosTypography>
								<SovosRadioGroup
									row
									options={[
										{
											label: REP_EXTENSION,
											value: REP_EXTENSION,
										},
										{
											label: TXT_EXTENSION,
											value: TXT_EXTENSION,
										},
									]}
									value={xstoreSettings.extension}
									onChange={(_: any, value: string) => {
										setXstoreSettings({
											...xstoreSettings,
											extension: value,
										});
									}}
									sx={{ mb: 3, ml: 1 }}
								/>
							</>
						)}
					</>
				) : (
					<SovosLinearProgress />
				)}

				<SovosSnackbar
					open={snackbar.open}
					onClose={() => setSnackbar({ ...snackbar, message: '', open: false })}
					variant={snackbar.variant}
					message={snackbar.message}
					timeout='short'
				/>

				<SovosDialog open={exitConfirmationDialog.isOpen}>
					<SovosDialogTitle>Are you sure you want to leave?</SovosDialogTitle>
					<SovosDialogContent>
						<SovosTypography>You have unsaved changes.</SovosTypography>
						<SovosTypography>
							Your changes will be lost if you don't save them.
						</SovosTypography>
					</SovosDialogContent>
					<SovosDialogActions>
						<SovosButton
							onClick={() => {
								if (exitConfirmationDialog.continue)
									exitConfirmationDialog.continue();
							}}
							variant='text'
						>
							Leave page
						</SovosButton>
						<SovosButton
							onClick={() =>
								updateConfigurationMutation.mutate(undefined, {
									onSuccess: () => {
										setExitConfirmationDialog({
											isOpen: false,
											continue: null,
										});
									},
								})
							}
							disabled={
								!(isValid && hasChanges) ||
								updateConfigurationMutation.isLoading
							}
						>
							{updateConfigurationMutation.isLoading ? (
								<SovosCircularProgress size={20} />
							) : (
								'Save changes'
							)}
						</SovosButton>
					</SovosDialogActions>
				</SovosDialog>
			</SovosPageContentGeneric>
		</SovosPageLayout>
	);
};

export default SubscriptionSettingsPage;
